import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, setAdminViewUserId, setOnUnauthorized } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminViewUser, setAdminViewUserState] = useState(null); // { userId, hoTen, email }

  const forceLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
    setAdminViewUserState(null);
    setAdminViewUserId(null);
  };

  useEffect(() => {
    setOnUnauthorized(forceLogout);
    return () => setOnUnauthorized(null);
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Cookie httpOnly được trình duyệt tự gửi theo request
      // Không cần đọc localStorage nữa
      const response = await authAPI.getMe();
      if (response.data.success) {
        setUser(response.data.data);
        setIsAuthenticated(true);
      }
    } catch {
      // 401 = chưa đăng nhập hoặc cookie hết hạn
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, matKhau) => {
    try {
      const response = await authAPI.login({ email, matKhau });
      if (response.data.success) {
        // Cookie httpOnly được backend set tự động — không lưu token trong JS
        setUser(response.data.data.user);
        setIsAuthenticated(true);
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Có lỗi xảy ra khi đăng nhập!'
      };
    }
  };

  const register = async (hoTen, email, matKhau) => {
    try {
      const response = await authAPI.register({ hoTen, email, matKhau });
      if (response.data.success) {
        setUser(response.data.data.user);
        setIsAuthenticated(true);
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Có lỗi xảy ra khi đăng ký!'
      };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout(); // Backend xóa cookie
    } catch {
      // ignore lỗi mạng khi logout
    }
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const setAdminViewUser = (targetUser) => {
    setAdminViewUserState(targetUser);
    setAdminViewUserId(targetUser ? targetUser.userId : null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      login,
      register,
      logout,
      updateUser,
      adminViewUser,
      setAdminViewUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
